import React, { useEffect, useState } from "react";
import { Sparkles, Flame } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * "AI is thinking" loading state shown while search results are being fetched.
 * Cycles through short status steps so the wait reads as intelligent work
 * rather than a generic spinner.
 *
 * Two visual variants:
 * - default: cool spinning gradient ring (purple/blue) — the LLM re-rank path.
 * - elastic: a warm ember "heat ripple" (amber/orange/red) shown when the
 *   Elasticsearch engine is serving results. Deliberately very different so the
 *   active engine is obvious at a glance.
 */
const STEPS = [
  "Understanding your search",
  "Scanning the catalog",
  "Ranking the best matches",
];

const ELASTIC_STEPS = [
  "Searching the catalog",
  "Matching your terms",
  "Gathering results",
];

const STEP_INTERVAL_MS = 1400;

export const AiRerankLoading: React.FC<{
  className?: string;
  /**
   * When the re-rank ran on the OpenAI backup, tint the little bouncing dots
   * pink — a subtle, secret tell that we've failed over off Claude.
   */
  isOpenAi?: boolean;
  /** Elasticsearch is serving results — show the warm ember variant. */
  isElastic?: boolean;
  /**
   * Active rerank model (e.g. "claude-sonnet-4-5-20250929" or "gpt-..."), if a
   * rerank is running. Shown plainly as "ES + Claude"/"ES + GPT" for now — a
   * placeholder until the pink-on-OpenAI color tell is the only signal needed.
   */
  rerankModel?: string;
}> = ({ className, isOpenAi = false, isElastic = false, rerankModel }) => {
  const steps = isElastic ? ELASTIC_STEPS : STEPS;
  const modelLabel = rerankModel?.toLowerCase().startsWith("gpt")
    ? "GPT"
    : rerankModel
      ? "Claude"
      : null;
  const elasticHeadline = modelLabel ? `ES + ${modelLabel}` : "Searching the catalog";
  const [step, setStep] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setStep((s) => (s + 1) % steps.length),
      STEP_INTERVAL_MS,
    );
    return () => clearInterval(id);
  }, [steps.length]);

  const stepLabel = (
    <div className="mt-1 h-5 text-sm text-muted-foreground">
      <span
        key={step}
        className="inline-block duration-300 animate-in fade-in slide-in-from-bottom-1"
      >
        {steps[step]}…
      </span>
    </div>
  );

  // Warm ember variant — concentric heat ripples radiating from a flame core,
  // with a warm shimmering progress bar. No blue/purple anywhere. Swaps to the
  // same pink/fuchsia used for the default variant's OpenAI-backup dots when
  // the ES rerank has failed over off Claude — same "secret tell" convention.
  if (isElastic) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center py-16 text-center",
          className,
        )}
      >
        <div className="relative mb-6 flex h-20 w-20 items-center justify-center">
          {/* Radiating heat ripples */}
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={cn(
                "absolute h-16 w-16 rounded-full border-2 animate-ping",
                isOpenAi ? "border-fuchsia-400/60" : "border-orange-400/60",
              )}
              style={{ animationDelay: `${i * 500}ms`, animationDuration: "1.8s" }}
            />
          ))}
          {/* Warm glow */}
          <span
            className={cn(
              "absolute h-14 w-14 rounded-full blur-xl animate-pulse",
              isOpenAi ? "bg-fuchsia-500/30" : "bg-amber-500/30",
            )}
          />
          {/* Flame core */}
          <span
            className={cn(
              "relative flex h-12 w-12 items-center justify-center rounded-full shadow-lg",
              isOpenAi
                ? "bg-gradient-to-br from-fuchsia-400 via-pink-500 to-rose-500 shadow-fuchsia-500/40"
                : "bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 shadow-orange-500/40",
            )}
          >
            <Flame className="h-6 w-6 text-white animate-pulse" />
          </span>
        </div>

        <div
          className={cn(
            "bg-clip-text text-base font-semibold text-transparent",
            isOpenAi
              ? "bg-gradient-to-r from-fuchsia-500 via-pink-500 to-rose-500"
              : "bg-gradient-to-r from-amber-500 via-orange-500 to-red-500",
          )}
        >
          {elasticHeadline}
        </div>

        {stepLabel}

        {/* Warm shimmer bar instead of bouncing dots */}
        <div
          className={cn(
            "mt-4 h-1.5 w-40 overflow-hidden rounded-full",
            isOpenAi ? "bg-fuchsia-500/15" : "bg-orange-500/15",
          )}
        >
          <div
            className={cn(
              "h-full w-1/2 animate-[shimmer_1.4s_ease-in-out_infinite] rounded-full bg-gradient-to-r",
              isOpenAi
                ? "from-fuchsia-400 via-pink-500 to-rose-500"
                : "from-amber-400 via-orange-500 to-red-500",
            )}
          />
        </div>

        <style>{`
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(220%); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center py-16 text-center",
        className,
      )}
    >
      <div className="relative mb-6 flex items-center justify-center">
        {/* Soft pulsing glow */}
        <div className="absolute h-16 w-16 rounded-full bg-violet-400/30 blur-xl animate-pulse" />
        {/* Spinning gradient ring with a sparkle core */}
        <div className="relative h-16 w-16 rounded-full bg-gradient-to-tr from-sky-400 via-violet-500 to-fuchsia-500 p-[2px] animate-spin [animation-duration:2.5s]">
          <div className="flex h-full w-full items-center justify-center rounded-full bg-background">
            <Sparkles className="h-7 w-7 text-violet-500 animate-pulse" />
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-sky-500 via-violet-500 to-fuchsia-500 bg-clip-text text-base font-semibold text-transparent">
        Finding your best matches
      </div>

      {stepLabel}

      <div className="mt-4 flex gap-1.5">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={cn(
              "h-2 w-2 animate-bounce rounded-full",
              // Pink (violet-leaning fuchsia) dots are the secret tell that the
              // OpenAI backup is active.
              isOpenAi ? "bg-fuchsia-500/70" : "bg-violet-500/70",
            )}
            style={{ animationDelay: `${i * 150}ms` }}
          />
        ))}
      </div>
    </div>
  );
};
